import { Ref, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import Button from "./button";
import Text from "./text";
import useTheme from "../hooks/theme";

export type TabItem<T extends string = string> = {
  value: T;
  label: string;
  icon?: ReactNode;
};

type TabsProps<T extends string = string> = {
  value?: T;
  items: TabItem<T>[];
  onChange?: (value: T) => void;
  className?: string;
  centered?: boolean;
  scrollerRef?: Ref<HTMLDivElement>;
  variant?: "default" | "pill";
};

const Tabs = <T extends string = string>({
  value,
  items,
  onChange,
  className,
  centered,
  scrollerRef,
  variant = "default",
}: TabsProps<T>) => {
  const theme = useTheme();
  const pill = variant === "pill";
  const activeIndex = Math.max(
    0,
    items.findIndex((item) => item.value === value),
  );

  return (
    <div
      ref={scrollerRef}
      className={twMerge(
        "flex w-full min-w-0 flex-1 flex-nowrap items-center overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        pill
          ? "gap-[0.8rem]"
          : "relative rounded-full bg-surface-primary p-[0.4rem] shadow-[inset_0_2px_4px_rgba(17,17,17,0.14)]",
        centered && pill && "justify-center",
        className,
      )}
    >
      {!pill && items.length > 0 && (
        <span
          aria-hidden
          className="pointer-events-none absolute top-[0.4rem] h-[4rem] rounded-full bg-action-primary transition-transform duration-300 ease-out motion-reduce:transition-none"
          style={{
            left: "0.4rem",
            width: `calc((100% - 0.8rem) / ${items.length})`,
            transform: `translateX(${activeIndex * 100}%)`,
          }}
        />
      )}
      {items.map((item) => {
        const active = item.value === value;
        if (pill)
          return (
            <Button
              key={item.value}
              shape="round"
              outlined={!active}
              title={item.label}
              icon={item.icon}
              color={active ? "primary" : "secondary"}
              minWidth="3.2rem"
              data-tab={item.value}
              className="shrink-0 whitespace-nowrap"
              onClick={() => onChange?.(item.value)}
            />
          );

        return (
          <button
            key={item.value}
            type="button"
            data-tab={item.value}
            onClick={() => onChange?.(item.value)}
            className="relative z-[1] inline-flex h-[4rem] min-w-0 flex-1 cursor-pointer items-center justify-center gap-[0.6rem] whitespace-nowrap border-0 bg-transparent px-[1.2rem]"
          >
            {item.icon}
            <Text
              children={item.label}
              weight={600}
              color={
                active ? theme.colors.onAccent : theme.colors.font.secondary
              }
            />
          </button>
        );
      })}
    </div>
  );
};

export default Tabs;
